import { useState, useEffect } from 'react'
import { format } from 'date-fns'

import { Specific } from '@/common/ui/Specific'
import { Card } from '@/common/ui/Card'
import { MediaGallery } from '@/common/ui/MediaGallery'
import { ExpandableText } from '@/common/ui/ExpandableText'
import { Button } from '@/common/ui/Form/Button'
import { Icon } from '@/common/ui/Icon'
import { resolveRelativeAssetUrl } from '@/common/lib/assetsUrl'
import { ProductAssetsGallery } from '@/app/entities/product-deprecated/ui/ProductAssetsGallery'
import { useCurrentOrg } from '@/app/entities/org/model'

import { useProduct } from '../../model'
import { ProductBrandModal, PRODUCT_BRAND_MODAL_ID } from './ProductBrandModal'
import { ProductSuggestedBrandPopup } from './ProductSuggestedBrandPopup'

const getProductImageUrl = (image: { [key: string]: unknown }): string | null => {
    if (!('big' in image)) {
        return null
    }
    const big = image.big
    return typeof big === 'string' ? big : null
}

export type ProductDataProps = {
    suggestedBrand?: string | null
    analyticsGenerationId?: string | null
    isAnalyticsCompleted?: boolean
}

export const ProductData = ({
    suggestedBrand,
    analyticsGenerationId,
    isAnalyticsCompleted = false,
}: ProductDataProps = {}) => {
    const [activeGallery, setActiveGallery] = useState<'product' | 'assets' | null>(null)
    const [selectedMediaIndex, setMediaSelectedIndex] = useState<number>(0)

    const { product } = useProduct()
    const { data: currentOrg } = useCurrentOrg()

    const normalizedImages360 =
        product?.images360
            ?.filter((url): url is string => Boolean(url))
            ?.map(resolveRelativeAssetUrl)
            .filter((url): url is string => Boolean(url)) ?? []

    const allMedia = [
        ...(product?.video?.map((url, index) => ({ type: 'video' as const, url, originalIndex: index })) || []),
        ...(normalizedImages360.length > 0
            ? [
                  {
                      type: 'image' as const,
                      url: normalizedImages360[0],
                      originalIndex: 0,
                      frames360: normalizedImages360,
                  },
              ]
            : []),
        ...(product?.images?.flatMap((image, index) => {
            const url = resolveRelativeAssetUrl(getProductImageUrl(image) as string)
            if (!url) {
                return []
            }
            return [{ type: 'image' as const, url, originalIndex: index }]
        }) || []),
    ]

    // При первой загрузке автоматически выбираем первый элемент из медиа карточки товара
    useEffect(() => {
        if (allMedia.length > 0 && activeGallery === null) {
            const firstImage = allMedia.find(media => media.type === 'image')
            if (firstImage) {
                const firstImageIndex = allMedia.indexOf(firstImage)
                handleProductMediaSelect(firstImageIndex)
            } else if (allMedia.length > 0) {
                handleProductMediaSelect(0)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allMedia.length])

    if (!product) return null

    const handleProductMediaSelect = (index: number) => {
        setActiveGallery('product')
        setMediaSelectedIndex(index)
    }
    const handleAssetsMediaSelect = (index: number) => {
        setActiveGallery('assets')
        setMediaSelectedIndex(index)
    }

    return (
        <>
            {/* Уведомление */}
            {/* <div className='bg-[rgba(255,196,53,0.1)] border border-white/5 box-border p-5 relative rounded-xl w-full hidden'>
                <div className='flex flex-col gap-4 items-start relative'>
                    <p className='font-normal leading-normal relative text-[#ffd267] text-sm w-full'>
                        Это копия карточки на момент импорта. Своевременно обновляйте данные, чтобы анализ, тексты и
                        графика были точнее.
                    </p>
                </div>
                <button
                    onClick={handleCloseNotification}
                    className='absolute right-5 size-4 top-1/2 -translate-y-1/2 hover:opacity-70 transition-opacity'
                >
                    <CloseIcon />
                </button>
            </div> */}

            <Card type='product' className='w-full'>
                <h3 className='font-medium leading-[22px] text-lg text-white flex-1'>Название</h3>
                <p className='text-[var(--pixorion-gray-2)]'>{product.name}</p>
            </Card>

            <Card type='product' className='w-full'>
                <h3 className='font-medium leading-[22px] text-lg text-white'>Основное</h3>
                <div className='flex flex-col gap-5 row-gap-[12px] items-start relative w-full'>
                    {(
                        [
                            { label: 'Маркетплейс', value: product.marketplace },
                            { label: 'Артикул МП', value: product.marketplace_id },
                            { label: 'Бренд', value: product.brand?.name ?? 'Не указан', isBrand: true },
                            ...(product.seller
                                ? [
                                      {
                                          label: 'Продавец',
                                          value: product.seller.name ?? 'Не указан',
                                          href: product.seller.marketplace_url,
                                      },
                                  ]
                                : []),
                            { label: 'Категория', value: product.category },
                            { label: 'Артикул продавца', value: product.vendor_id },
                        ] as Array<{ label: string; value?: string | null; isBrand?: boolean; href?: string | null }>
                    )
                        .filter(item => item.value)
                        .reduce(
                            (
                                rows: Array<
                                    Array<{ label: string; value: string; isBrand?: boolean; href?: string | null }>
                                >,
                                item,
                                index
                            ) => {
                                const rowIndex = Math.floor(index / 3)
                                if (!rows[rowIndex]) {
                                    rows[rowIndex] = []
                                }
                                rows[rowIndex].push(
                                    item as { label: string; value: string; isBrand?: boolean; href?: string | null }
                                )
                                return rows
                            },
                            []
                        )
                        .map((row, rowIndex) => (
                            <div key={rowIndex} className='flex gap-8 items-start relative w-full'>
                                {row.map((item, itemIndex) => (
                                    <div
                                        key={itemIndex}
                                        className='flex flex-col row-gap-[13px] items-start flex-1 relative'
                                    >
                                        <p className='elative text-[#aba7af] w-full text-sm leading-[22px]'>
                                            {item.label}
                                        </p>
                                        {item.isBrand ? (
                                            <ProductSuggestedBrandPopup
                                                suggestedBrand={suggestedBrand}
                                                generationId={analyticsGenerationId}
                                                productId={product.id}
                                                currentBrandName={product.brand?.name}
                                                isAnalyticsCompleted={isAnalyticsCompleted}
                                            >
                                                <div className='flex items-center gap-2'>
                                                    <p className='leading-normal relative text-white text-sm'>
                                                        {item.value}
                                                    </p>
                                                    <Button
                                                        as='label'
                                                        htmlFor={PRODUCT_BRAND_MODAL_ID}
                                                        size='sm'
                                                        variant='ghost'
                                                        emphasis='accent-secondary'
                                                        aria-label='Изменить бренд'
                                                        title='Изменить бренд'
                                                        className='cursor-pointer'
                                                    >
                                                        <Icon name='edit-2' size='sm' />
                                                    </Button>
                                                </div>
                                            </ProductSuggestedBrandPopup>
                                        ) : item.href ? (
                                            <a
                                                href={item.href}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className='leading-normal relative text-white text-sm underline decoration-white/30 underline-offset-2 hover:decoration-white'
                                                aria-label={`${item.label}: ${item.value}`}
                                            >
                                                {item.value}
                                            </a>
                                        ) : (
                                            <p className='leading-normal relative text-white w-full text-sm'>
                                                {item.value}
                                            </p>
                                        )}
                                    </div>
                                ))}
                                {Array.from({ length: 3 - row.length }).map((_, emptyIndex) => (
                                    <div key={`empty-${emptyIndex}`} className='flex-1' />
                                ))}
                            </div>
                        ))}
                </div>
            </Card>

            <ProductBrandModal currentBrand={product.brand ?? null} />

            <Card type='product' className='w-full'>
                <MediaGallery
                    allMedia={allMedia}
                    selectedMediaIndex={activeGallery === 'product' ? selectedMediaIndex : -1}
                    onMediaSelect={handleProductMediaSelect}
                    title='Медиа–файлы из карточки товара'
                />
            </Card>

            {currentOrg && product.id && (
                <Card type='product' className='w-full'>
                    <ProductAssetsGallery
                        orgId={currentOrg.id}
                        productId={product.id}
                        selectedMediaIndex={activeGallery === 'assets' ? selectedMediaIndex : -1}
                        onMediaSelect={handleAssetsMediaSelect}
                        // onSelectedImageChange={imageUrl => {
                        //     if (onSelectedImageChange) {
                        //         onSelectedImageChange(imageUrl, 'assets')
                        //     }
                        // }}
                    />
                </Card>
            )}

            <Card type='product' className='w-full'>
                <div className='flex flex-col gap-4 items-start w-full'>
                    <div className='flex gap-[8px] items-center w-full'>
                        <h3 className='basis-0 font-medium grow leading-[22px] text-lg text-white '>Описание</h3>
                    </div>
                    <ExpandableText text={product.description || ''} markdown wordLimit={30} showCharacterCount />
                </div>
            </Card>

            {/* Теги */}
            {/* <Card type='product' className='w-full'>
                <h3 className='font-medium leading-[22px] relative text-lg text-white'>
                    Теги на маркетплейсе
                </h3>
                <div className='flex gap-[6px] items-start relative w-full'>
                    {(productData?.tags || ['прозрачный', 'защита']).map((tag, index) => (
                        <div
                            key={index}
                            className='bg-[rgba(171,167,175,0.2)] box-border flex gap-[10px] h-[30px] items-center justify-center px-4 relative rounded-full'
                        >
                            <span className='font-medium leading-[22px] relative text-sm text-center text-white whitespace-nowrap'>
                                {tag}
                            </span>
                        </div>
                    ))}
                </div>
            </Card> */}

            {/* Дополнительная информация */}
            <Card type='product' className='w-full'>
                <h3 className='font-medium leading-[22px] relative text-lg text-white'>Дополнительная информация</h3>
                <Specific
                    characteristics={product.characteristics?.map(characteristic => ({
                        title: characteristic.name,
                        value: characteristic.value,
                    }))}
                />
            </Card>

            <Card type='product' className='w-full'>
                <h3 className='font-medium leading-[22px] relative text-lg text-white'>Метаданные источника</h3>
                <Specific
                    characteristics={[
                        {
                            title: 'Создано на МП',
                            value: product.created_at && format(new Date(product.created_at), 'dd.MM.yyyy, HH:mm'),
                        },
                        {
                            title: 'Изменено на МП',
                            value: product.updated_at && format(new Date(product.updated_at), 'dd.MM.yyyy, HH:mm'),
                        },
                        // {
                        //     title: 'Последний импорт в Pixarion',
                        //     value: product.last_import && format(new Date(product.last_import), 'dd.MM.yyyy, HH:mm'),
                        // },
                    ]}
                />
            </Card>

            {/* История обновлений импорта */}
            {/*<Card type='product' className='w-full'>
                <h3 className='font-medium leading-[22px] relative text-lg text-white'>
                    История обновлений импорта
                </h3>
                <div className='flex flex-col items-end relative w-full'>
                    {(
                        productData?.importHistory || [
                            { type: 'update' as const, date: '02.10.2025, 14:38' },
                            { type: 'update' as const, date: '28.09.2025, 20:52' },
                            { type: 'update' as const, date: '21.09.2025, 12:19' },
                            { type: 'update' as const, date: '15.09.2025, 14:52' },
                            { type: 'import' as const, date: '10.09.2025, 10:17' },
                        ]
                    ).map((item, index) => (
                        <div
                            key={index}
                            className={`box-border flex items-center px-[10px] relative w-full ${
                                index % 2 === 0 ? 'bg-[#46434a]' : ''
                            }`}
                        >
                            <div className='flex gap-[5px] items-center relative'>
                                <div className='flex gap-[10px] items-center px-[10px] py-4 relative'>
                                    <div className='relative size-[14px]'>
                                        {item.type === 'import' ? <ImportIcon /> : <RefreshIcon />}
                                    </div>
                                </div>
                            </div>
                            <div className='flex gap-[5px] flex-1 items-center relative'>
                                <div className='flex gap-[10px] flex-1 items-center px-[10px] py-4 relative'>
                                    <p className='font-normal leading-4 relative text-sm text-white whitespace-nowrap'>
                                        {item.type === 'import' ? 'Импорт' : 'Обновление'}
                                    </p>
                                </div>
                            </div>
                            <div className='flex gap-[5px] flex-1 items-center justify-end relative'>
                                <div className='flex gap-[10px] flex-1 items-center justify-center px-[10px] py-4 relative'>
                                    <p className='flex-1 font-normal leading-4 relative text-[#aba7af] text-sm text-right'>
                                        {item.date}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
            */}
        </>
    )
}
